import { useDatepicker, useMonth } from '@datepicker-react/hooks';
import getHours from 'date-fns/getHours';
import getMinutes from 'date-fns/getMinutes';
import getSeconds from 'date-fns/getSeconds';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import set from 'date-fns/set';
import subDays from 'date-fns/subDays';
import { useEffect } from 'react';
import styled from 'styled-components';

import { useIsMobile } from 'app/theme/useBreakpoints';
import { DatePickerHeader } from 'pkg/components/DateTimePicker/DatePicker/DatePickerHeader';
import { Day } from 'pkg/components/DateTimePicker/DatePicker/Day';

interface DatePickerProps {
  date?: Date;
  setDate?: (date: Date) => void;
}

const DayLabel = styled.div`
  display: flex;
  justify-content: center;
  font-weight: 600;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.palette.gray.dividerGray};
`;

const DaysGrid = styled.div<{ isMobile?: boolean }>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  align-content: center;
  justify-items: center;
  grid-row-gap: ${({ isMobile }) => (isMobile ? '1rem' : '0.2rem')};
`;

const currentDate = new Date();
const month = currentDate.getMonth();
const year = currentDate.getFullYear();

export const DatePicker = ({ date, setDate }: DatePickerProps): JSX.Element => {
  if (!date || !setDate) {
    throw new Error('This component must be wrapped by DateTimePicker or explicitly be given props');
  }
  const isMobile = useIsMobile();

  const {
    activeMonths: [activeMonth],
    goToDate,
    goToNextMonthsByOneMonth,
    goToPreviousMonthsByOneMonth,
  } = useDatepicker({
    startDate: date,
    endDate: date,
    focusedInput: null,
    onDatesChange: () => {},
    numberOfMonths: 1,
  });

  useEffect(() => {
    goToDate(date);
  }, [date, goToDate]);

  const { days, monthLabel, weekdayLabels } = useMonth({
    year: activeMonth.year,
    month: activeMonth.month,
    firstDayOfWeek: 0,
  });

  const handleDateChange = (newDate: Date) => () =>
    setDate(set(newDate, { hours: getHours(date), minutes: getMinutes(date), seconds: getSeconds(date) }));

  return (
    <div>
      <DatePickerHeader
        goToNextMonth={goToNextMonthsByOneMonth}
        goToPreviousMonth={goToPreviousMonthsByOneMonth}
        isPreviousMonthActive={month === activeMonth.month && year === activeMonth.year}
      >
        {monthLabel}
      </DatePickerHeader>

      <DaysGrid isMobile={isMobile}>
        {weekdayLabels.map((label) => (
          <DayLabel key={label}>{label}</DayLabel>
        ))}
      </DaysGrid>

      <DaysGrid isMobile={isMobile}>
        {days.map((day, index) =>
          typeof day === 'object' ? (
            <Day
              isActive={isBefore(subDays(new Date(), 1), day.date)}
              isSelected={Boolean(isSameDay(date, day.date))}
              onClick={handleDateChange(day.date)}
              key={day.dayLabel}
              day={day}
            />
          ) : (
            // eslint-disable-next-line
            <div key={index} />
          )
        )}
      </DaysGrid>
    </div>
  );
};
